import React, { useEffect, useState } from "react";
import { useStepFormApi, BasicStepForm } from "components/StepForm";
import { ProgressSpinner } from "primereact/progressspinner";
import { BreadCrumb } from 'primereact/breadcrumb';
import { useNavigate, useParams } from "react-router-dom";
import { IPackageItem, requestAcceptPackageItemOffer, requestGetPackageDetails, requestGetPackageItems, requestRejectPackageItemOffer, requestSwitchPackageToDropOff, requestSwitchPackageToPickup } from "./api";
import { PendingDropoff } from "./components/PendingDropoff";
import { DroppedOff } from "./components/DroppedOff";
import { UnderReview } from "./components/UnderReview";
import { Received } from "./components/Received";
import { Reviewed } from "./components/Reviewed";
import { Completed } from "./components/Completed";
import { IPackage } from "../List/api";
import { calculateSellerEarningOnPrice, SELLER_PACKAGE_STATES } from "definitions";
import { getUrlToListPackagesPage } from "pages/urls";
import { usePackageItemNegotiationState } from './hooks';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { confirmDialog } from 'primereact/confirmdialog';
import { cs } from "utils/classnames";

export function ViewPackagePage() {
  const { id } = useParams();
  const navigateTo = useNavigate();

  if (!id) throw new Error("ID is required.");

  const [packageDetails, setPackageDetails] = useState<IPackage | null>(null);
  const [packageItems, setPackageItems] = useState<IPackageItem[] | null>(null);
  const [loadingPackageDetails, setLoadingPackageDetails] = useState<boolean>(false);

  useEffect(() => {
    loadPackageDetails();
  }, [])

  const loadPackageDetails = async () => {
    setLoadingPackageDetails(true);
    const response = await requestGetPackageDetails(id);

    const {packageItems} = await requestGetPackageItems(id);
    setPackageItems(packageItems);

    setPackageDetails(response);
    setLoadingPackageDetails(false);
  }

  const reloading = loadingPackageDetails && !!packageDetails
  const loadingFirstTime = loadingPackageDetails && !packageDetails

  return (
    <div
      style={{ maxWidth: "25rem" }}
      className="px-2 flex flex-column align-items-center mx-auto w-full"
    >
      <div className="w-full">
        <div className="not-printable">
          <BreadCrumb
            className="border-0"
            model={[{ label: "Packages", command: () => navigateTo(getUrlToListPackagesPage()) }, { label: packageDetails?.name || "Package" }]}
            home={{ icon: "pi pi-box", command: () => navigateTo(getUrlToListPackagesPage()) }}
          />
        </div>
        <h2 className="text-center">
          <i className={cs('pi text-xl', { 'pi-box': !reloading, 'pi-spinner pi-spin': reloading })} />{' '}
          Package Details
        </h2>
        {
          loadingFirstTime ? (
            <div className="text-center">
              <ProgressSpinner />
            </div>
          ) : null
        }
        {
          packageDetails ? (
            <PackageDetails
              item={packageDetails}
              packageItems={packageItems || []}
              onPackageDetailsChanged={loadPackageDetails}
            />
          ) : null
        }
      </div>
    </div>
  );
}

function PackageDetails(props: { item: IPackage, packageItems: IPackageItem[], onPackageDetailsChanged: () => any }) {
  const [switchingSendMode, setSwitchingSendMode] = useState(false);
  const { acceptItem, rejectItem, changePriceOfItem, getStatusById, getChangedPriceById } = usePackageItemNegotiationState(
    (item, price) => requestAcceptPackageItemOffer(item._id, price),
    item => requestRejectPackageItemOffer(item._id)
  );
  const {item: propItem, packageItems, onPackageDetailsChanged} = props;
  const [item, setItem] = useState(propItem);

  useEffect(() => {
    setItem(propItem)
  }, [propItem])

  const packageItemsWithStatus = packageItems.map(item => {
    const changedPrice = getChangedPriceById(item._id);
    const pricePatches = changedPrice
      ? { price: changedPrice, grossSellerEarning: calculateSellerEarningOnPrice(changedPrice, item.commissionTier) }
      : {}
    return { ...item, ...getStatusById(item._id), ...pricePatches }
  })

  const handleAcceptItem = acceptItem;
  const handleRejectItem = (item: IPackageItem) => {
    confirmDialog({
      message: (
        <div>
          <div>Are you sure you want to reject this offer?</div>
          <div>This item will not be listed in our shop.</div>
        </div>
      ),
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => rejectItem(item)
    });
  }

  const handleRequestPickup = async () => {
    confirmDialog({
      message: 'Are you sure you want to switch to Home Pickup?',
      header: 'Confirmation',
      icon: 'pi pi-question-circle',
      acceptClassName: 'p-button-warning',
      accept: async () => {
        setSwitchingSendMode(true)
        const updatedPackage = await requestSwitchPackageToPickup(item._id)
        setItem(item => ({ ...item, shouldSchedulePickup: updatedPackage.shouldSchedulePickup }))
        setSwitchingSendMode(false)
        onPackageDetailsChanged()
      }
    });
  }

  const handleRequestSelfDropoff = async () => {
    confirmDialog({
      message: 'Are you sure you want to switch to Self Drop?',
      header: 'Confirmation',
      icon: 'pi pi-question-circle',
      acceptClassName: 'p-button-warning',
      accept: async () => {
        setSwitchingSendMode(true)
        const updatedPackage = await requestSwitchPackageToDropOff(item._id)
        setItem(item => ({ ...item, shouldSchedulePickup: updatedPackage.shouldSchedulePickup }))
        setSwitchingSendMode(false)
        onPackageDetailsChanged()
      }
    });
  }

  const steps = [
    {
      name: SELLER_PACKAGE_STATES.PENDING_DROPOFF,
      Component: () => (
        <PendingDropoff
          item={item}
          onRequestPickup={handleRequestPickup}
          onRequestSelfDrop={handleRequestSelfDropoff}
          switchingSendMode={switchingSendMode}
        />
      )
    },
    {
      name: SELLER_PACKAGE_STATES.DROPPED_OFF,
      Component: () => (
        <DroppedOff item={item} />
      )
    },
    {
      name: SELLER_PACKAGE_STATES.RECEIVED_AT_WAREHOUSE,
      Component: () => (
        <Received item={item} />
      )
    },
    {
      name: SELLER_PACKAGE_STATES.UNDER_REVIEW,
      element: (
        <UnderReview
          item={item}
          packageItems={packageItemsWithStatus}
          onAcceptItem={handleAcceptItem}
          onRejectItem={handleRejectItem}
          onItemPriceChanged={changePriceOfItem}
        />
      )
    },
    {
      name: SELLER_PACKAGE_STATES.REVIEWED,
      element: <Reviewed
        item={item}
        packageItems={packageItemsWithStatus}
        onAcceptItem={handleAcceptItem}
        onRejectItem={handleRejectItem}
        onItemPriceChanged={changePriceOfItem}
      />
    },
    {
      name: SELLER_PACKAGE_STATES.COMPLETED,
      element: <Completed
        item={item}
        packageItems={packageItemsWithStatus}
        onAcceptItem={handleAcceptItem}
        onRejectItem={handleRejectItem}
        onItemPriceChanged={changePriceOfItem}
      />
    }
  ];
  
  const stepFormApi = useStepFormApi(steps, true);

  useEffect(() => {
    stepFormApi.gotoStep(steps.findIndex(step => step.name === item.status));
  }, [])

  return (
    <>
      <ConfirmDialog />
      <div className={cs({ 'opacity-60 pointer-events-none': switchingSendMode })}>
        <BasicStepForm stepFormApi={stepFormApi} hideButtons={true} />
      </div>
    </>
  );
}
